/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Controlled topbar dropdown; the parent owns the open state so only one is open at a time.
 *
 * <p>A popover under its trigger on wide screens; below 600px the stylesheet docks it as a
 * full-width sheet under the header. A folded menu (its trigger hidden inside "More") still opens
 * from the parent's state, so the wrapper is `display: contents` there and the popover hangs from
 * the header's edge.
 */
export function Menu({
  open,
  onOpenChange,
  trigger,
  label,
  returnFocus,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: (state: { open: boolean; toggle: () => void }) => ReactNode;
  /** Accessible name of the popover. */
  label?: string;
  /**
   * Where Escape sends focus when the menu's own trigger cannot take it: a menu folded into
   * "More" opens with its trigger hidden, so focus goes back to the More button instead.
   */
  returnFocus?: () => HTMLElement | null;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Opened from outside its own trigger, the first option takes focus so the keyboard lands
  // inside: a menu folded into "More" opens as More closes (taking the focused item with it), and
  // a page's "Select plant" action stays focused while the hidden trigger turns focus down. That
  // action is where Escape sends focus back.
  useEffect(() => {
    if (!open) return;
    const wrapper = ref.current as HTMLDivElement;
    const active = document.activeElement as HTMLElement;
    openerRef.current = null;
    if (wrapper.contains(active)) return;
    if (active !== document.body) openerRef.current = active;
    wrapper.querySelector<HTMLElement>('.menu-pop :is(button, [href], input)')?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onOpenChange(false);
      // Hand focus back to the trigger, so a keyboard user carries on from where they opened it.
      // A hidden trigger does not take focus, which is when the page action that opened the menu,
      // then `returnFocus`, stand in for it.
      const trigger = ref.current?.querySelector<HTMLElement>('[aria-haspopup]');
      for (const back of [trigger, openerRef.current, returnFocus?.()]) {
        back?.focus();
        if (back && document.activeElement === back) return;
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange, returnFocus]);

  return (
    <div className={cn('menu', className)} ref={ref}>
      {trigger({ open, toggle: () => onOpenChange(!open) })}
      {open && (
        <div className="menu-pop" role={label ? 'group' : undefined} aria-label={label}>
          {children}
        </div>
      )}
    </div>
  );
}
